/**
 * Endringssett DK — Sensor-integritetsforpliktelse (onboarding-flyt)
 *
 * Gjelder for pure sensor (role='sensor') OG hybrid (is_sensor=true).
 * Forpliktelsen gjelder på tvers av alle organisasjoner brukeren sensurerer for.
 * Må bekreftes første gangs innlogging som sensor; re-signeres ved ny mal-versjon.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { authenticatedFetch } from '@/lib/auth';
import {
  CheckCircle2, Loader2, UserCheck, Scale, Lock, FileSignature, Scroll, AlertTriangle, Sparkles
} from 'lucide-react';

interface Template {
  id: string;
  template_name: string;
  content: string;
  version: string;
}

interface SensorPoint { n: number; heading: string; confirm: string; }
interface SensorCategory { id: string; title: string; icon: typeof UserCheck; color: string; points: SensorPoint[]; }

// Speiler malen 20261104000002_sensor_integrity_contract.sql — 16 punkter i 5 kategorier.
// Nummereringen MÅ matche templatens for revisjons-spor.
const CATEGORIES: SensorCategory[] = [
  {
    id: 'A', title: 'Habilitet og rolle', icon: UserCheck, color: 'kf-primary',
    points: [
      { n: 1, heading: 'Ingen interessekonflikt', confirm: 'Jeg sensurerer ikke kandidater jeg har personlig, økonomisk eller annen relasjon til, og varsler kursarrangør ved tvil.' },
      { n: 2, heading: 'Faglig kompetanse', confirm: 'Jeg sensurerer kun innenfor fagområder der jeg har tilstrekkelig kompetanse, og varsler kursarrangør hvis innlevering ligger utenfor.' },
      { n: 3, heading: 'Egen dobbeltrolle', confirm: 'Som hybrid (sensor + medlem/admin i samme org) er jeg bevisst på at sensur skal være uavhengig av organisasjonens interesser, og registrerer habilitet-erklæring per kurs når aktuelt.' },
    ]
  },
  {
    id: 'B', title: 'Objektivitet og rettferdighet', icon: Scale, color: 'kf-primary',
    points: [
      { n: 4, heading: 'Konsekvent vurderingsstandard', confirm: 'Jeg bruker samme standard for alle kandidater, basert på faglige kriterier — ikke personlige preferanser, politiske syn, kjønn, alder, etnisitet eller lignende.' },
      { n: 5, heading: 'Rubric og vurderingskriterier', confirm: 'Jeg bruker vurderingsmatrisen konsekvent når den finnes, og gir skriftlig begrunnelse ved avvik på 15+ poeng fra KI-foreslått score.' },
      { n: 6, heading: 'Anonymitet respekteres', confirm: 'Jeg forsøker ikke å avdekke identiteten til anonymt sensurerte kandidater, og lar ikke identitets-hint påvirke vurderingen.' },
    ]
  },
  {
    id: 'C', title: 'Konfidensialitet og personvern', icon: Lock, color: 'kf-primary',
    points: [
      { n: 7, heading: 'Taushetsplikt', confirm: 'Jeg behandler alle innleveringer, resultater og kandidat-info konfidensielt, også etter at sensur-oppdraget er avsluttet.' },
      { n: 8, heading: 'GDPR og personvern', confirm: 'Jeg håndterer persondata i tråd med GDPR, og laster ikke ned eller kopierer kandidat-innleveringer til private enheter uten gyldig grunn.' },
      { n: 9, heading: 'Sikker tilgang', confirm: 'Jeg beskytter min innloggingstilgang (Vipps-konto, passord), låner ikke bort sensor-kontoen, og lar ikke andre sensurere på mine vegne.' },
    ]
  },
  {
    id: 'D', title: 'Dokumentasjon og varsling', icon: FileSignature, color: 'kf-primary',
    points: [
      { n: 10, heading: 'Begrunnelse ved klage og alternativ vurdering', confirm: 'Jeg gir saklig, faglig fundert skriftlig begrunnelse når det kreves (klage, alt. vurdering, KI-avvik over 15 poeng).' },
      { n: 11, heading: 'Varsling av uregelmessigheter', confirm: 'Jeg varsler kursarrangør ved mistanke om juks, plagiat eller ikke-tillatt KI-hjelp, og tar ikke selvstendige disiplinærtiltak.' },
      { n: 12, heading: 'Tidsrammer', confirm: 'Jeg leverer sensur innen avtalt frist, eller varsler kursarrangør i god tid dersom det ikke er mulig.' },
    ]
  },
  {
    id: 'E', title: 'Lovmessige og etiske krav', icon: Scroll, color: 'kf-primary',
    points: [
      { n: 13, heading: 'Lovmessig etterlevelse', confirm: 'Jeg overholder voksenopplæringsloven, forskrift om studieforbund, GDPR og andre relevante lover og forskrifter.' },
      { n: 14, heading: 'Rapportering av misbruk', confirm: 'Jeg varsler Kompetanseforbundet umiddelbart ved oppdagede sikkerhetshull, utilsiktet datatilgang eller tilsvarende.' },
      { n: 15, heading: 'Faglig utvikling', confirm: 'Jeg holder min faglige kompetanse oppdatert og deltar i påkrevde kurs/orienteringer.' },
      { n: 16, heading: 'Signering og erkjennelse', confirm: 'Jeg erkjenner at brudd kan medføre tap av sensur-tilgang og evt. strafferettslig oppfølging, og at min bekreftelse loggføres med tidspunkt, IP og bruker-id.' },
    ]
  },
];

const ALL_POINT_NUMBERS = CATEGORIES.flatMap(c => c.points.map(p => p.n));

export default function SensorIntegrityOnboarding() {
  const navigate = useNavigate();
  const [template, setTemplate] = useState<Template | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState<Set<number>>(new Set());
  const [showFullText, setShowFullText] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [statusRes, tplRes] = await Promise.all([
          authenticatedFetch('/api/onboarding/status').then(r => r.json()),
          authenticatedFetch('/api/onboarding/sensor-template').then(r => r.json())
        ]);
        if (!statusRes.success) throw new Error(statusRes.error || 'Kunne ikke hente status');
        if (!tplRes.success) throw new Error(tplRes.error || 'Kunne ikke hente mal');
        setTemplate(tplRes.template);

        // Hvis ikke required: hopp rett videre
        if (!statusRes.sensor_contract?.required) {
          navigate('/dashboard', { replace: true });
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const togglePoint = (n: number) => {
    const next = new Set(confirmed);
    if (next.has(n)) next.delete(n); else next.add(n);
    setConfirmed(next);
  };
  const toggleCategory = (cat: SensorCategory) => {
    const next = new Set(confirmed);
    const allInCat = cat.points.every(p => next.has(p.n));
    cat.points.forEach(p => {
      if (allInCat) next.delete(p.n); else next.add(p.n);
    });
    setConfirmed(next);
  };

  const allConfirmed = ALL_POINT_NUMBERS.every(n => confirmed.has(n));
  const progressPct = Math.round((confirmed.size / ALL_POINT_NUMBERS.length) * 100);

  const handleSubmit = async () => {
    if (!allConfirmed) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await authenticatedFetch('/api/onboarding/sensor-acknowledge', { method: 'POST' });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Signering feilet');
      // Suksess — videre til dashboard eller sensor-view
      navigate('/sensor/dashboard', { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-kf-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          <div className="font-semibold mb-1 flex items-center gap-2"><AlertTriangle className="h-5 w-5" />Feil</div>
          <div className="text-sm">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-3 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-kf-primary to-kf-primary/90 text-white rounded-lg p-4 sm:p-6 shadow">
        <div className="flex items-start gap-3 sm:gap-4">
          <div className="bg-white/20 p-2 sm:p-3 rounded-lg flex-shrink-0">
            <Sparkles className="h-6 w-6 sm:h-7 sm:w-7" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold leading-tight">Sensor-integritetsforpliktelse</h1>
            <p className="text-white/90 mt-2 text-sm sm:text-base">
              Som sensor forvalter du ansvaret for rettferdig og faglig forsvarlig vurdering.
              Ditt arbeid danner grunnlaget for utstedte kompetanseattester som er rettslig bindende dokumenter.
            </p>
            <p className="text-white/90 mt-2 text-sm">
              <strong>Denne forpliktelsen gjelder all din sensur-aktivitet på plattformen, uavhengig av organisasjon.</strong>
            </p>
            {template?.version?.startsWith('UTKAST') && (
              <div className="mt-3 inline-flex items-start gap-1.5 bg-yellow-400/20 border border-yellow-300/50 rounded px-2 py-1 text-xs">
                <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                <span>UTKAST: juridisk gjennomgang før prod ({template.version})</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Fremdrift */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
        <div className="flex items-center justify-between mb-2 gap-2">
          <div className="text-sm font-medium text-gray-700">
            {confirmed.size} av {ALL_POINT_NUMBERS.length} bekreftet
          </div>
          <div className="text-sm font-semibold text-kf-primary flex-shrink-0">{progressPct}%</div>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div className="bg-kf-primary h-2 rounded-full transition-all" style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      {/* Kategorier */}
      {CATEGORIES.map(cat => {
        const Icon = cat.icon;
        const catCount = cat.points.filter(p => confirmed.has(p.n)).length;
        const catComplete = catCount === cat.points.length;
        return (
          <div key={cat.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className={`px-3 sm:px-5 py-3 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 ${catComplete ? 'bg-emerald-50' : 'bg-gray-50'}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className={`p-2 rounded flex-shrink-0 ${catComplete ? 'bg-emerald-100 text-emerald-700' : 'bg-kf-primary/10 text-kf-primary'}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="text-base font-semibold text-gray-900 leading-tight">{cat.id}. {cat.title}</h2>
                  <p className="text-xs text-gray-600 mt-0.5">{catCount}/{cat.points.length} bekreftet</p>
                </div>
              </div>
              <button
                onClick={() => toggleCategory(cat)}
                className="text-xs font-medium text-kf-primary hover:underline self-start sm:self-auto px-2 py-1 -mx-2 -my-1 sm:p-0 sm:m-0 touch-manipulation"
              >
                {catComplete ? 'Fjern alle' : 'Hak av alle'}
                <span className="hidden sm:inline"> i kategorien</span>
              </button>
            </div>
            <div className="divide-y divide-gray-100">
              {cat.points.map(p => {
                const isConfirmed = confirmed.has(p.n);
                return (
                  <label key={p.n} className={`flex items-start gap-3 px-3 sm:px-5 py-3 cursor-pointer transition-colors ${isConfirmed ? 'bg-emerald-50/30' : 'hover:bg-gray-50'}`}>
                    <input
                      type="checkbox"
                      checked={isConfirmed}
                      onChange={() => togglePoint(p.n)}
                      className="mt-1 h-5 w-5 sm:h-4 sm:w-4 text-kf-primary rounded border-gray-300 focus:ring-kf-primary flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-gray-900">{p.n}. {p.heading}</div>
                      <div className="text-sm text-gray-700 mt-0.5">
                        <span className="text-gray-500 font-medium">Jeg bekrefter:</span> {p.confirm}
                      </div>
                    </div>
                    {isConfirmed && <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />}
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Full kontraktstekst (kollapsibel) */}
      {template && (
        <div className="bg-white border border-gray-200 rounded-lg">
          <button
            onClick={() => setShowFullText(!showFullText)}
            className="w-full px-3 sm:px-5 py-3 flex items-center justify-between text-left hover:bg-gray-50 gap-2"
          >
            <span className="text-sm font-medium text-gray-700">
              {showFullText ? 'Skjul' : 'Vis'} full kontraktstekst
            </span>
            <span className="text-xs text-gray-500 flex-shrink-0">v{template.version}</span>
          </button>
          {showFullText && (
            <div className="px-3 sm:px-5 py-4 border-t border-gray-200 prose prose-sm max-w-none overflow-x-auto">
              <ReactMarkdown>{template.content}</ReactMarkdown>
            </div>
          )}
        </div>
      )}

      {/* Signering — sticky på bunn, ekstra padding-bottom så den ikke overlapper innhold */}
      <div className="h-24 sm:h-20" aria-hidden="true" />
      <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-5 sticky bottom-2 sm:bottom-4 shadow-lg">
        {error && (
          <div className="mb-3 bg-red-50 border border-red-200 rounded p-2 text-sm text-red-800">{error}</div>
        )}
        <button
          onClick={handleSubmit}
          disabled={!allConfirmed || submitting}
          className="w-full inline-flex items-center justify-center gap-2 px-3 py-3 sm:px-4 bg-kf-primary hover:bg-kf-primary/90 text-white font-semibold rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed text-sm sm:text-base touch-manipulation"
        >
          {submitting && <Loader2 className="h-5 w-5 animate-spin flex-shrink-0" />}
          <span>
            {allConfirmed
              ? 'Bekreft og signer sensor-forpliktelsen'
              : `Bekreft alle ${ALL_POINT_NUMBERS.length} punktene (${confirmed.size}/${ALL_POINT_NUMBERS.length})`}
          </span>
        </button>
        <p className="text-xs text-gray-500 mt-2 text-center leading-snug">
          Ved signering loggføres tid, IP og bruker-id. Gjelder all sensur-aktivitet på plattformen.
        </p>
      </div>
    </div>
  );
}
